/**
 * Services for translating internal data-types to customer-facing data-types
 * @module
 */
var _ = require('optimizely-client-fn');

var di = require('core/di');
var freeze = require('core/utils/freeze');
var LazyProp = require('./lazy_prop');

var ChangeDataStore = di.get('stores/change_data');

/**
 * Translates a Decision to a CampaignDecision (references campaign instead of layer)
 * @param {Decision} decision
 * @returns {CampaignDecision}
 */
exports.translateDecisionToCampaignDecision = function(decision:Decision): CampaignDecision {
  return rename(_.cloneDeep(decision), {
    layerId: 'campaignId',
    isLayerHoldback: 'isCampaignHoldback',
  });
};

/**
 * Translates a LayerDecidedApiEvent to a CampaignDecidedApiEvent
 * @param {LayerDecidedApiEvent} layerDecidedEvent
 * @returns {CampaignDecidedApiEvent}
 */
exports.translateLayerEventToCampaignEvent = function(layerDecidedEvent:LayerDecidedApiEvent): CampaignDecidedApiEvent {
  // Reconstruct data property referencing campaign instead of layer
  var dataObject = {} as any;
  // Convert campaign only when necessary (perf)
  LazyProp.defineProperty(dataObject, 'campaign', function() {
    var campaign = convertLayerToCampaign(layerDecidedEvent.data.layer);
    if (__INVARIANT__) {
      require('core/utils/invariant').isType('Campaign', campaign);
    }
    return campaign;
  }, 'campaignEvent');
  dataObject.decisionTicket = layerDecidedEvent.data.decisionTicket;
  dataObject.decision = this.translateDecisionToCampaignDecision(layerDecidedEvent.data.decision);
  dataObject.audiences = layerDecidedEvent.data.audiences;

  return {
    type: 'lifecycle',
    name: 'campaignDecided',
    data: dataObject,
  };
};

/**
 * Translates a ViewActivatedEvent to a PageActivatedEvent
 * @param {ViewActivatedApiEvent} viewEvent
 * @returns {PageActivatedEvent}
 */
exports.translateViewActivatedToPageActivated = function(viewEvent:ViewActivatedApiEvent): PageActivatedEvent {
  return {
    type: 'lifecycle',
    name: 'pageActivated',
    data: {
      page: viewEvent.data.view,
    },
  };
};

function convertLayerToCampaign(layer) {
  var campaign = _.cloneDeep(layer);
  if (campaign.changes) {
    campaign.changes = _.map(campaign.changes, exports.dereferenceChangeId);
  }
  if (campaign.experiments) {
    _.each(campaign.experiments, function(experiment) {
      if (experiment.changes) {
        experiment.changes = _.map(experiment.changes, exports.dereferenceChangeId);
      }

      if (experiment.variations) {
        _.each(experiment.variations, function(variation) {
          if (variation.actions) {
            _.each(variation.actions, function(action) {
              if (action.changes) {
                action.changes = _.map(action.changes, exports.dereferenceChangeId);
              }
            });
          }
        });
      }
    });
  }

  return campaign;
}

/**
 * If the argument is the ID of a current change, return a frozen copy of that change; otherwise return the arg
 * @param {string|Change} changeOrChangeId
 */
exports.dereferenceChangeId = function(changeOrChangeId:string|Change) {
  var change = ChangeDataStore.getChange(changeOrChangeId);
  if (change) {
    return freeze.safeReference(change);
  }

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('Change', changeOrChangeId);
  }
  return changeOrChangeId;
};

/**
 * Renames an object property
 * @param {Object} obj The object whose property should be renamed
 * @param {Object} keyMap The keys and new names
 * @private
 */
var rename = function(obj:any, keyMap:any) {
  var result = _.omit(obj, _.keys(keyMap));
  _.each(keyMap, function(value, key) {
    result[value] = obj[key];
  });
  return result;
};




